import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Package, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatPrice, formatDate } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

export default function OrderConfirmation() {
  const { id } = useParams();
  const { t, lang } = useLanguage();
  const es = lang === "es";
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data.order)).catch(() => setOrder(null)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>;
  if (!order) return (
    <div className="min-h-[60vh] grid place-items-center text-center px-5">
      <div><h1 className="font-serif text-4xl mb-4">{es ? "Solicitud no encontrada" : "Request not found"}</h1><Link to="/shop"><Button className="rounded-none">{t("Continue shopping")}</Button></Link></div>
    </div>
  );

  const shipLabel = order.shipping_method === "express"
    ? (es ? "Envío exprés (1–2 días hábiles)" : "Express shipping (1–2 business days)")
    : (es ? "Envío normal (3–5 días hábiles)" : "Standard shipping (3–5 business days)");

  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-10 py-16" data-testid="order-confirmation-page">
      <div className="text-center">
        <CheckCircle2 className="h-14 w-14 text-gold mx-auto mb-6" strokeWidth={1} />
        <p className="overline text-gold mb-3">{es ? "Solicitud recibida" : "Request received"}</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-light">{es ? "¡Gracias por tu solicitud!" : "Thank you for your request!"}</h1>
        <p className="text-muted-foreground mt-4">
          {es
            ? "Nuestro equipo de M&A Jewelry revisará tu solicitud y se pondrá en contacto contigo por correo o teléfono para coordinar tu pedido."
            : "Our M&A Jewelry team will review your request and contact you by email or phone to arrange your order."}
        </p>
        <p className="mt-2 text-sm">{es ? "Solicitud" : "Request"} <span className="gold-text font-medium" data-testid="order-number">{order.order_number}</span> · {formatDate(order.created_at)}</p>
      </div>

      <div className="border border-border mt-12">
        <div className="p-6 border-b border-border flex items-center gap-3">
          <Package className="h-5 w-5 text-gold" />
          <div>
            <p className="text-sm font-medium">{shipLabel}</p>
            <p className="text-xs text-muted-foreground">
              {es ? "Enviaremos la confirmación a" : "We'll send confirmation to"} <span className="text-foreground">{order.customer?.email}</span>.
            </p>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {order.items.map((i, idx) => (
            <div key={idx} className="flex gap-4">
              <div className="w-16 h-20 bg-secondary overflow-hidden shrink-0"><img src={i.image} alt={i.name} className="h-full w-full object-cover" /></div>
              <div className="flex-1"><p className="font-serif text-lg leading-tight">{i.name}</p><p className="text-xs text-muted-foreground mt-1">{es ? "Cantidad" : "Qty"} {i.quantity}{i.size ? ` · ${i.size}` : ""}{i.color ? ` · ${i.color}` : ""}</p></div>
              <span className="text-sm">{formatPrice(i.price * i.quantity)}</span>
            </div>
          ))}
        </div>
        <div className="p-6 border-t border-border flex justify-between font-serif text-xl">
          <span>{es ? "Valor estimado" : "Estimated value"}</span>
          <span>{formatPrice(order.subtotal)}</span>
        </div>
      </div>

      <div className="flex gap-3 mt-10 justify-center">
        <Link to="/shop"><Button className="rounded-none h-12 px-8" data-testid="continue-shopping-btn">{t("Continue Shopping")}</Button></Link>
      </div>
    </div>
  );
}
